import { GeographicTilingScheme } from "@cesium/engine";
import combine from "../../Core/combine.js";
import Credit from "../../Core/Credit.js";
import defaultValue from "../../Core/defaultValue.js";
import defined from "../../Core/defined.js";
import DeveloperError from "../../Core/DeveloperError.js";
import Event from "../../Core/Event.js";
import Rectangle from "../../Core/Rectangle.js";
import Resource from "../../Core/Resource.js";
import ImageryProvider from "../../Scene/ImageryProvider.js";
import ConstantUrl from "../../../../widgets/Source/Ditap/icon/ConstantUrl.js";

const defaultParameters = Object.freeze({
  service: "WMTS",
  version: "1.0.0",
  request: "GetTile",
});

/**
 * 다이탭 국내 정사영상 이미지 제공 클래스.</br>
 * Class providing nation orthophoto imagery in DiTAP.
 *
 *
 * @alias NationwideOrthoImageryProvider
 * @constructor
 
 *
 *
 *
 * @example
 * 
  // DitapViewer 생성
  const viewer = new Ditap.DitapViewer("ditapContainer", {
    baseLayerPicker: true,
  });

  // DitapBaseLayerPicker 인스턴스 생성
  const ditapBaseLayerPicker = viewer.ditapBaseLayerPicker;

  const ditapImageryProvider = new Ditap.ProviderViewModel({
    name: "Ditap Korea Map",
    iconUrl: getUrlFromBase64(
      Ditap.ConstantUrl.DITAP_MAP_IMG_URL
    ),
    tooltip: "Ditap Korea Map",
    category: "Other",
    creationFunction: function () {
      return new Ditap.NationwideOrthoImageryProvider();
    },
  });
  
  ditapBaseLayerPicker.viewModel.selectedImagery = ditapImageryProvider;
 *
 *
 */
function NationwideOrthoImageryProvider(options) {
  options = defaultValue(options, {});

  /*
   * The default alpha blending value of this provider, with 0.0 representing fully transparent and
   * 1.0 representing fully opaque.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultAlpha = undefined;

  /*
   * The default alpha blending value on the night side of the globe of this provider, with 0.0 representing fully transparent and
   * 1.0 representing fully opaque.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultNightAlpha = undefined;

  /*
   * The default alpha blending value on the day side of the globe of this provider, with 0.0 representing fully transparent and
   * 1.0 representing fully opaque.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultDayAlpha = undefined;

  /*
   * The default brightness of this provider.  1.0 uses the unmodified imagery color.  Less than 1.0
   * makes the imagery darker while greater than 1.0 makes it brighter.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultBrightness = undefined;

  /*
   * The default contrast of this provider.  1.0 uses the unmodified imagery color.  Less than 1.0 reduces
   * the contrast while greater than 1.0 increases it.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultContrast = undefined;

  /*
   * The default hue of this provider in radians. 0.0 uses the unmodified imagery color.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultHue = undefined;

  /*
   * The default saturation of this provider. 1.0 uses the unmodified imagery color. Less than 1.0 reduces the
   * saturation while greater than 1.0 increases it.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultSaturation = undefined;

  /*
   * The default gamma correction to apply to this provider.  1.0 uses the unmodified imagery color.
   *
   * @type {number|undefined}
   * @default undefined
   */
  this.defaultGamma = undefined;

  /*
   * The default texture minification filter to apply to this provider.
   *
   * @type {TextureMinificationFilter}
   * @default undefined
   */
  this.defaultMinificationFilter = undefined;

  /*
   * The default texture magnification filter to apply to this provider.
   *
   * @type {TextureMagnificationFilter}
   * @default undefined
   */
  this.defaultMagnificationFilter = undefined;

  // const resource = Resource.createIfNeeded(
  //   `${Utils.getProtocol()}//121.135.139.45:18081/geoserver/gwc/service/wmts?layer=merge%3Acloud_all&style=&tilematrixset=EPSG%3A4326&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fpng&TileMatrix=EPSG%3A4326%3A{TileMatrix}&TileCol={TileCol}&TileRow={TileRow}`
  // );
  const resource = Resource.createIfNeeded(
    `${ConstantUrl.DITAP_RESOURCE_BASE_URL}/geoserver/gwc/service/wmts?layer=merge%3Acloud_all&style=&tilematrixset=EPSG%3A4326&Service=WMTS&Request=GetTile&Version=1.0.0&Format=image%2Fpng&TileMatrix=EPSG%3A4326%3A{TileMatrix}&TileCol={TileCol}&TileRow={TileRow}`
  );

  const style = "raster";
  const tileMatrixSetID = "EPSG:4326";
  const url = resource.url;

  const bracketMatch = url.match(/{/g);
  if (
    !defined(bracketMatch) ||
    (bracketMatch.length === 1 && /{s}/.test(url))
  ) {
    resource.setQueryParameters(defaultParameters);
    this._useKvp = true;
  } else {
    const templateValues = {
      style: style,
      Style: style,
      TileMatrixSet: tileMatrixSetID,
    };

    resource.setTemplateValues(templateValues);
    this._useKvp = false;
  }

  this._resource = resource;
  this._layer = "merge:cloud_all";
  this._style = style;
  this._tileMatrixSetID = tileMatrixSetID;
  this._tileMatrixLabels = undefined;
  this._format = "image/jpeg";
  this._tileDiscardPolicy = undefined;

  this._tilingScheme = new GeographicTilingScheme();
  this._tileWidth = 256;
  this._tileHeight = 256;

  this._minimumLevel = 0;
  this._maximumLevel = 17;

  this._rectangle = this._tilingScheme.rectangle;
  this._dimensions = undefined;

  this._reload = undefined;

  this._readyPromise = Promise.resolve(true);

  // Check the number of tiles at the minimum level.  If it's more than four,
  // throw an exception, because starting at the higher minimum
  // level will cause too many tiles to be downloaded and rendered.
  const swTile = this._tilingScheme.positionToTileXY(
    Rectangle.southwest(this._rectangle),
    this._minimumLevel
  );
  const neTile = this._tilingScheme.positionToTileXY(
    Rectangle.northeast(this._rectangle),
    this._minimumLevel
  );
  const tileCount =
    (Math.abs(neTile.x - swTile.x) + 1) * (Math.abs(neTile.y - swTile.y) + 1);
  //>>includeStart('debug', pragmas.debug);
  if (tileCount > 4) {
    throw new DeveloperError(
      `The imagery provider's rectangle and minimumLevel indicate that there are ${tileCount} tiles at the minimum level. Imagery providers with more than four tiles at the minimum level are not supported.`
    );
  }
  //>>includeEnd('debug');

  this._errorEvent = new Event();

  const credit = new Credit("© Heliosen Co., Ltd., CC-BY-ND");

  this._credit = typeof credit === "string" ? new Credit(credit) : credit;

  this._subdomains = undefined;
  this._subdomains = ["a", "b", "c"];
}

function requestImage(imageryProvider, col, row, level, request, interval) {
  const labels = imageryProvider._tileMatrixLabels;
  const tileMatrix = defined(labels) ? labels[level] : level.toString();
  const subdomains = imageryProvider._subdomains;
  const staticDimensions = imageryProvider._dimensions;
  const dynamicIntervalData = defined(interval) ? interval.data : undefined;

  let resource;
  let templateValues;
  if (!imageryProvider._useKvp) {
    templateValues = {
      TileMatrix: tileMatrix,
      TileRow: row.toString(),
      TileCol: col.toString(),
      s: subdomains[(col + row + level) % subdomains.length],
    };

    resource = imageryProvider._resource.getDerivedResource({
      request: request,
    });
    resource.setTemplateValues(templateValues);

    if (defined(staticDimensions)) {
      resource.setTemplateValues(staticDimensions);
    }

    if (defined(dynamicIntervalData)) {
      resource.setTemplateValues(dynamicIntervalData);
    }
  } else {
    // build KVP request
    let query = {};
    query.tilematrix = tileMatrix;
    query.layer = imageryProvider._layer;
    query.style = imageryProvider._style;
    query.tilerow = row;
    query.tilecol = col;
    query.tilematrixset = imageryProvider._tileMatrixSetID;
    query.format = imageryProvider._format;

    if (defined(staticDimensions)) {
      query = combine(query, staticDimensions);
    }

    if (defined(dynamicIntervalData)) {
      query = combine(query, dynamicIntervalData);
    }

    templateValues = {
      s: subdomains[(col + row + level) % subdomains.length],
    };

    resource = imageryProvider._resource.getDerivedResource({
      queryParameters: query,
      request: request,
    });
    resource.setTemplateValues(templateValues);
  }

  return ImageryProvider.loadImage(imageryProvider, resource);
}

Object.defineProperties(NationwideOrthoImageryProvider.prototype, {
  /*
   * Gets the URL of the service hosting the imagery.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {string}
   * @readonly
   */
  url: {
    get: function () {
      return this._resource.url;
    },
  },

  /*
   * Gets the proxy used by this provider.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {Proxy}
   * @readonly
   */
  proxy: {
    get: function () {
      return this._resource.proxy;
    },
  },

  /*
   * Gets the width of each tile, in pixels. This function should
   * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {number}
   * @readonly
   */
  tileWidth: {
    get: function () {
      return this._tileWidth;
    },
  },

  /*
   * Gets the height of each tile, in pixels.  This function should
   * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {number}
   * @readonly
   */
  tileHeight: {
    get: function () {
      return this._tileHeight;
    },
  },

  /*
   * Gets the maximum level-of-detail that can be requested.  This function should
   * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {number|undefined}
   * @readonly
   */
  maximumLevel: {
    get: function () {
      return this._maximumLevel;
    },
  },

  /*
   * Gets the minimum level-of-detail that can be requested.  This function should
   * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {number}
   * @readonly
   */
  minimumLevel: {
    get: function () {
      return this._minimumLevel;
    },
  },

  /*
   * Gets the tiling scheme used by this provider.  This function should
   * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {TilingScheme}
   * @readonly
   */
  tilingScheme: {
    get: function () {
      return this._tilingScheme;
    },
  },

  /*
   * Gets the rectangle, in radians, of the imagery provided by this instance.  This function should
   * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {Rectangle}
   * @readonly
   */
  rectangle: {
    get: function () {
      return this._rectangle;
    },
  },

  /*
   * Gets the tile discard policy.  If not undefined, the discard policy is responsible
   * for filtering out "missing" tiles via its shouldDiscardImage function.  If this function
   * returns undefined, no tiles are filtered.  This function should
   * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {TileDiscardPolicy}
   * @readonly
   */
  tileDiscardPolicy: {
    get: function () {
      return this._tileDiscardPolicy;
    },
  },

  /*
   * Gets an event that is raised when the imagery provider encounters an asynchronous error.  By subscribing
   * to the event, you will be notified of the error and can potentially recover from it.  Event listeners
   * are passed an instance of {@link TileProviderError}.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {Event}
   * @readonly
   */
  errorEvent: {
    get: function () {
      return this._errorEvent;
    },
  },

  /*
   * Gets the mime type of images returned by this imagery provider.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {string}
   * @readonly
   */
  format: {
    get: function () {
      return this._format;
    },
  },

  /*
   * Gets a value indicating whether or not the provider is ready for use.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {boolean}
   * @readonly
   */
  ready: {
    value: true,
  },

  /*
   * Gets a promise that resolves to true when the provider is ready for use.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {Promise<boolean>}
   * @readonly
   */
  readyPromise: {
    get: function () {
      return this._readyPromise;
    },
  },

  /*
   * Gets the credit to display when this imagery provider is active.  Typically this is used to credit
   * the source of the imagery.  This function should not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {Credit}
   * @readonly
   */
  credit: {
    get: function () {
      return this._credit;
    },
  },

  /*
   * Gets a value indicating whether or not the images provided by this imagery provider
   * include an alpha channel.  If this property is false, an alpha channel, if present, will
   * be ignored.  If this property is true, any images without an alpha channel will be treated
   * as if their alpha is 1.0 everywhere.  When this property is false, memory usage
   * and texture upload time are reduced.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {boolean}
   * @readonly
   */
  hasAlphaChannel: {
    get: function () {
      return true;
    },
  },
  /*
   * Gets or sets a clock that is used to get keep the time used for time dynamic parameters.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {Clock}
   */
  clock: {
    get: function () {
      return this._timeDynamicImagery.clock;
    },
    set: function (value) {
      this._timeDynamicImagery.clock = value;
    },
  },
  /*
   * Gets or sets a time interval collection that is used to get time dynamic parameters. The data of each
   * TimeInterval is an object containing the keys and values of the properties that are used during
   * tile requests.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {TimeIntervalCollection}
   */
  times: {
    get: function () {
      return this._timeDynamicImagery.times;
    },
    set: function (value) {
      this._timeDynamicImagery.times = value;
    },
  },
  /*
   * Gets or sets an object that contains static dimensions and their values.
   * @memberof NationwideOrthoImageryProvider.prototype
   * @type {object}
   */
  dimensions: {
    get: function () {
      return this._dimensions;
    },
    set: function (value) {
      if (this._dimensions !== value) {
        this._dimensions = value;
        if (defined(this._reload)) {
          this._reload();
        }
      }
    },
  },
});

/*
 * Gets the credits to be displayed when a given tile is displayed.
 *
 * @param {number} x The tile X coordinate.
 * @param {number} y The tile Y coordinate.
 * @param {number} level The tile level;
 * @returns {Credit[]} The credits to be displayed when the tile is displayed.
 *
 * @exception {DeveloperError} <code>getTileCredits</code> must not be called before the imagery provider is ready.
 */
NationwideOrthoImageryProvider.prototype.getTileCredits = function (
  x,
  y,
  level
) {
  return undefined;
};

/*
 * Requests the image for a given tile.  This function should
 * not be called before {@link NationwideOrthoImageryProvider#ready} returns true.
 *
 * @param {number} x The tile X coordinate.
 * @param {number} y The tile Y coordinate.
 * @param {number} level The tile level.
 * @param {Request} [request] The request object. Intended for internal use only.
 * @returns {Promise<ImageryTypes>|undefined} A promise for the image that will resolve when the image is available, or
 *          undefined if there are too many active requests to the server, and the request should be retried later.
 *
 * @exception {DeveloperError} <code>requestImage</code> must not be called before the imagery provider is ready.
 */
NationwideOrthoImageryProvider.prototype.requestImage = function (
  x,
  y,
  level,
  request
) {
  let result;
  const timeDynamicImagery = this._timeDynamicImagery;
  let currentInterval;

  // Try and load from cache
  if (defined(timeDynamicImagery)) {
    currentInterval = timeDynamicImagery.currentInterval;
    result = timeDynamicImagery.getFromCache(x, y, level, request);
  }

  // Couldn't load from cache
  if (!defined(result)) {
    result = requestImage(this, x, y, level, request, currentInterval);
  }

  // If we are approaching an interval, preload this tile in the next interval
  if (defined(result) && defined(timeDynamicImagery)) {
    timeDynamicImagery.checkApproachingInterval(x, y, level, request);
  }

  return result;
};

/*
 * Picking features is not currently supported by this imagery provider, so this function simply returns
 * undefined.
 *
 * @param {number} x The tile X coordinate.
 * @param {number} y The tile Y coordinate.
 * @param {number} level The tile level.
 * @param {number} longitude The longitude at which to pick features.
 * @param {number} latitude  The latitude at which to pick features.
 * @return {undefined} Undefined since picking is not supported.
 */
NationwideOrthoImageryProvider.prototype.pickFeatures = function (
  x,
  y,
  level,
  longitude,
  latitude
) {
  return undefined;
};
export default NationwideOrthoImageryProvider;
